import type { ReactNode } from 'react';
import { Link } from 'react-router';

import type { BoardReleaseOperationView } from '../../client/types.js';
import {
  externalReleaseLinkProps,
  releaseStatusPresentation,
  releaseText,
  releaseTimestampPresentation,
  safeBoardReleaseOperations,
  safeReleaseRoutePath,
  sortBoardReleaseOperations,
} from '../../logic/releases.js';
import { Chip } from './state.js';

/** Shared card/detail projection. It intentionally renders every operation. */
export function BoardReleaseOperations({
  operations,
  compact,
}: {
  readonly operations: readonly BoardReleaseOperationView[] | undefined;
  readonly compact: boolean;
}): ReactNode {
  const sorted = sortBoardReleaseOperations(safeBoardReleaseOperations(operations));
  if (sorted.length === 0) return null;

  return (
    <ul
      className={compact ? 'board-release-operations board-release-operations-compact' : 'board-release-operations'}
      role="list"
      aria-label="Deployment and release history"
    >
      {sorted.map((operation, index) => {
        const deploymentPath = safeReleaseRoutePath(operation.deploymentPath, 'deployments');
        const releasePath = safeReleaseRoutePath(operation.releasePath, 'releases');
        const status = releaseStatusPresentation(operation.status);
        const created = releaseTimestampPresentation(operation.createdAt);
        const updated = releaseTimestampPresentation(operation.updatedAt);
        const target = operation.target as Partial<BoardReleaseOperationView['target']> | null;
        const publicUrl = externalReleaseLinkProps(target?.publicUrl);
        return (
          <li key={`${releaseText(operation.deploymentId, 'deployment')}-${String(index)}`}>
            <div className="board-release-summary">
              <span className="board-release-target">
                {publicUrl === null ? (
                  releaseText(target?.displayName)
                ) : (
                  <a className="board-release-target-link" {...publicUrl}>
                    {releaseText(target?.displayName)}
                  </a>
                )}
                <span>{target?.type === 'production' ? 'Production' : target?.type === 'stage' ? 'Stage' : 'Unknown type'}</span>
              </span>
              <Chip tone={status.tone} text={status.text} label={`Deployment status ${status.text}`} />
            </div>
            <div className="board-release-links">
              {deploymentPath === null ? (
                <span>Deployment link unavailable</span>
              ) : (
                <Link to={deploymentPath} aria-label={`Open deployment ${releaseText(operation.deploymentId)}`}>
                  Deployment
                </Link>
              )}
              {operation.releaseId === null ? null : releasePath === null ? (
                <span>Release link unavailable</span>
              ) : (
                <Link to={releasePath} aria-label={`Open release ${releaseText(operation.releaseId)}`}>
                  Release
                </Link>
              )}
              {created.dateTime === null ? (
                <span>Created {created.text}</span>
              ) : (
                <span>Created <time dateTime={created.dateTime}>{created.text}</time></span>
              )}
              {updated.dateTime === null ? (
                <span>Updated {updated.text}</span>
              ) : (
                <span>Updated <time dateTime={updated.dateTime}>{updated.text}</time></span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
